/**
 * practiceGuard — the switch that makes a TEST MEETING unable to save.
 *
 * `installPracticeGuard(client)` adds one request interceptor to an axios
 * client. While no test meeting is open it does nothing at all. While one is
 * open (`activatePractice(engine)`), every request from that client is routed:
 *
 *   1. The engine SERVES it → the screen gets the engine's reply; nothing is
 *      sent. This is every write a meeting screen makes, and the reads of the
 *      practice meeting itself.
 *   2. A READ the engine declines → it goes to the server as normal, and the
 *      engine may OVERLAY the reply (a raised issue appears in the issue list).
 *   3. A WRITE the engine declines → REFUSED with a 409 and a plain sentence,
 *      when it touches a meeting domain (`PRACTICE_GUARDED_PREFIXES`) or names
 *      a practice id. Every other write (a timer, a rail pin, a phone call) is
 *      left alone — the rest of the app keeps working while you test.
 *
 * WHY A GUARD AND NOT A FLAG IN EVERY COMPONENT. The meeting screens embed
 * other lanes' components with their own write paths (the next-quarter form,
 * the People grid, the issue modal, the to-do dialogs). A test meeting that
 * relied on each of them remembering to check a flag would save the first time
 * one forgot. Catching the request itself is the only place "nothing saves"
 * can be true by construction.
 *
 * The clients that carry the guard (each calls `installPracticeGuard` right
 * after it is created): `services/apiClient.ts` (the shared client — api.ts,
 * todosApi, eosQuarterlyApi, wallApi, capacityApi, fathomHubApi…),
 * `services/eosL10Api.ts`, `services/kickoffApi.ts`, `services/metricRegistryApi.ts`
 * and `services/projectsHubApi.ts`. That list came from a full inventory of
 * every request the two meeting roots can fire, nested modals included
 * (2026-09-17). A NEW client that a meeting screen writes through must install
 * the guard too, or a test meeting will save through it.
 *
 * CLOSING HAS A GRACE PERIOD. Some screens save on their way out — the People
 * grid flushes its debounced PUT in an unmount cleanup, and React runs a
 * parent's cleanup before its children's, so the host's "close the test
 * meeting" would otherwise land a moment BEFORE that last save and let it
 * through to the real session. `activatePractice`'s close therefore waits
 * `PRACTICE_CLOSE_GRACE_MS` before letting requests pass again — long enough
 * for an unmount flush or a debounce that outlives its screen (≤ 1.2s), short
 * enough that nobody leaves a test meeting and makes a real save inside it.
 */
import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { PracticeEngine } from './practiceEngine';
import { PRACTICE_ID_PREFIX, type HttpMethod, type PracticeReply, type PracticeRequest } from './types';

/** Shown verbatim by `extractApiError` wherever a screen reports a failed write. */
export const PRACTICE_REFUSAL =
  'Test meeting — nothing is saved here, and this action is not part of the test run.';

/**
 * Write paths a test meeting must never send. A write here that the engine does
 * not simulate is REFUSED. Two groups:
 *
 *   · the meeting domains themselves;
 *   · everything a meeting screen can reach through its nested windows — a
 *     to-do's detail (with its email and SMS composers and the build
 *     scheduler), the project that to-do turns into, the contact card inside
 *     that, and the scorecard's cell editor and targets sheet.
 *
 * Deliberately broad: a missing prefix is a real save (or a real email) during
 * a test meeting, while an extra one only refuses an unrelated action taken in
 * the few minutes a test meeting is open. Kept OFF the list, on purpose: the
 * softphone's live-call updates (/api/communications/calls), the timer widget
 * (/api/time-tracking, except starting one from a project), rail pins and
 * notifications — app chrome that must keep working while you test.
 */
export const PRACTICE_GUARDED_PREFIXES: readonly string[] = [
  // the meeting domains
  '/api/eos',                                // /api/eos, /api/eos-l10, /api/eos-quarterly
  '/api/scorecard',                          // the next-quarter plan commit
  '/api/metric',                             // /api/metrics/:id/values + annotations, /api/metric-registry
  '/api/settings/quarterly-targets',         // the wall's targets sheet (?targets=1)
  '/api/todos',
  '/api/processes',
  // reachable from a meeting screen's nested windows
  '/api/capacity',                           // build scheduler: confirm / free build blocks
  '/api/inbox',                              // compose, schedule, drafts, inline images
  '/api/communications/sms',                 // the to-do SMS composer's send
  '/api/phones/sms',                         // scheduled texts
  '/api/projects',                           // the project a to-do turns into
  '/api/documentation',
  '/api/flowcharts',
  '/api/crm',                                // the contact card inside a project
  '/api/custom-properties',
  '/api/contact-tags',
  '/api/fathom',                             // linking / triaging calls from a project
  '/api/time-tracking/start-for-client',     // the project modal's timer button
];

/** How long a closed test meeting keeps catching requests (see the header). */
export const PRACTICE_CLOSE_GRACE_MS = 1500;

/**
 * GETs that WRITE on the server. Refused while a test meeting is open, exactly
 * like an unsimulated write — a read is otherwise always allowed through.
 */
export const PRACTICE_REFUSED_READS: readonly RegExp[] = [
  // The to-do email composer's draft read mints a booking invite (projectTodoEmailService).
  /^\/api\/projects\/todos\/[^/]+\/email-draft$/,
];

/** The open test meeting — set by the latest activation, identified by its token. */
let active: { engine: PracticeEngine; token: object } | null = null;
/** A test meeting that has closed but still catches ITS OWN requests until `until`. */
let closing: { engine: PracticeEngine; until: number } | null = null;
const guarded = new WeakSet<AxiosInstance>();

/**
 * Open a test meeting: the guard routes requests to `engine` until the returned
 * close function runs. Closing starts a grace period (`graceMs`, default
 * `PRACTICE_CLOSE_GRACE_MS`; 0 closes at once) during which only requests that
 * NAME this test meeting — its meeting id, its session, a practice id — are
 * still caught, so an exit save lands in the engine while the page you left for
 * works normally at once.
 *
 * Each activation carries its own token, so a close can only end the
 * activation that returned it: React StrictMode's mount → unmount → mount runs
 * two activations of the same engine, and the first one's close must not switch
 * off the second.
 */
export function activatePractice(engine: PracticeEngine, opts: { graceMs?: number } = {}): () => void {
  const token = {};
  active = { engine, token };
  closing = null;
  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    if (active?.token !== token) return;
    active = null;
    const grace = opts.graceMs ?? PRACTICE_CLOSE_GRACE_MS;
    if (grace <= 0) return;
    const slot = { engine, until: Date.now() + grace };
    closing = slot;
    setTimeout(() => { if (closing === slot) closing = null; }, grace);
  };
}

/** The closing engine, while its grace period lasts. */
function closingEngine(): PracticeEngine | null {
  if (!closing) return null;
  if (Date.now() >= closing.until) {
    closing = null;
    return null;
  }
  return closing.engine;
}

/** The test meeting catching requests right now — open, or closing within its grace — or null. */
export function getActivePractice(): PracticeEngine | null {
  return active?.engine ?? closingEngine();
}

/** Does this request name the test meeting — its meeting, its session, or anything the engine minted? */
export function namesTestMeeting(req: PracticeRequest, engine: PracticeEngine): boolean {
  let body = '';
  try {
    body = req.body === undefined ? '' : JSON.stringify(req.body);
  } catch {
    body = '';
  }
  const hay = `${req.path} ${body}`;
  const { meetingId, sessionId } = engine.seed;
  return hay.includes(meetingId)
    || (!!sessionId && hay.includes(sessionId))
    || hay.includes(PRACTICE_ID_PREFIX)
    || /\/todos\/9\d{8,}(\/|$)/.test(req.path);
}

const isWrite = (m: HttpMethod): boolean => m !== 'get' && m !== 'head' && m !== 'options';

function joinUrl(base: string, url: string): string {
  if (!base) return url;
  if (!url) return base;
  return `${base.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
}

function parseBody(data: unknown): any {
  if (data == null) return undefined;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) return undefined;
  return data;
}

/** Normalise an axios config into a PracticeRequest, or null when it is not an /api call. */
export function normalizeRequest(config: InternalAxiosRequestConfig): PracticeRequest | null {
  const method = String(config.method || 'get').toLowerCase() as HttpMethod;
  const url = String(config.url || '');
  const full = /^https?:\/\//i.test(url) ? url : joinUrl(String(config.baseURL || ''), url);
  let parsed: URL;
  try {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost';
    parsed = new URL(full, origin);
  } catch {
    return null;
  }
  const path = parsed.pathname.replace(/\/+$/, '') || '/';
  if (path !== '/api' && !path.startsWith('/api/')) return null;

  const query: Record<string, string> = {};
  parsed.searchParams.forEach((v, k) => { query[k] = v; });
  const params = config.params as unknown;
  if (params instanceof URLSearchParams) {
    params.forEach((v, k) => { query[k] = v; });
  } else if (params && typeof params === 'object') {
    for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
      if (v === undefined || v === null) continue;
      query[k] = Array.isArray(v) ? v.map(String).join(',') : String(v);
    }
  }

  return { method, path, query, body: parseBody(config.data) };
}

/** Would this write be refused if the engine declines it? */
export function mustRefuse(req: PracticeRequest): boolean {
  if (!isWrite(req.method)) return false;
  if (PRACTICE_GUARDED_PREFIXES.some(p => req.path === p || req.path.startsWith(p))) return true;
  if (req.path.includes(PRACTICE_ID_PREFIX)) return true;
  try {
    return req.body !== undefined && JSON.stringify(req.body).includes(PRACTICE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** Turn a reply into what an adapter owes axios: a response, or a rejection shaped like axios's own. */
function settle(config: InternalAxiosRequestConfig, reply: PracticeReply): AxiosResponse {
  const status = reply.status ?? 200;
  const response: AxiosResponse = {
    data: reply.data,
    status,
    statusText: status < 400 ? 'OK' : 'Test meeting',
    headers: {},
    config,
    request: undefined,
  };
  const validate = config.validateStatus;
  if (!validate || validate(status)) return response;
  throw new AxiosError(
    `Request failed with status code ${status}`,
    status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    config,
    undefined,
    response,
  );
}

function parseResponseBody(data: unknown): any {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

/** Add the guard to one axios client. Idempotent. */
export function installPracticeGuard(client: AxiosInstance): void {
  // A stubbed client (a test double without interceptors) simply goes unguarded.
  if (!client?.interceptors?.request?.use || guarded.has(client)) return;
  guarded.add(client);

  client.interceptors.request.use((config) => {
    // A config can come back through the interceptors (apiClient re-sends a
    // 429'd read with the same config). Wrap the ORIGINAL adapter once, or the
    // retry would run the engine twice and overlay its delta twice — and give
    // the original back if the test meeting has closed in the meantime.
    const marked = config as InternalAxiosRequestConfig & { __practiceAdapter?: InternalAxiosRequestConfig['adapter'] };
    const release = () => {
      if ('__practiceAdapter' in marked) {
        config.adapter = marked.__practiceAdapter;
        delete marked.__practiceAdapter;
      }
      return config;
    };

    const open = active?.engine ?? null;
    const engine = open ?? closingEngine();
    if (!engine) return release();
    const req = normalizeRequest(config);
    if (!req) return release();
    // Closing: only the test meeting's own late saves are still caught.
    if (!open && !namesTestMeeting(req, engine)) return release();

    if (!('__practiceAdapter' in marked)) marked.__practiceAdapter = config.adapter;
    const passThrough = axios.getAdapter(marked.__practiceAdapter ?? axios.defaults.adapter);

    config.adapter = async (cfg: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
      // Dispatch can lag the interceptor; if the test meeting is gone by then, send it as it was.
      if (getActivePractice() !== engine) return passThrough(cfg);

      let reply: PracticeReply | undefined;
      try {
        reply = await engine.serve(req);
      } catch (err: any) {
        // A bug in a simulated route must read as a failed action, not a crash —
        // and it must never fall through to the real server.
        reply = {
          status: 500,
          data: { success: false, error: `Test meeting: ${err?.message || 'this action could not be simulated.'}` },
        };
      }
      if (reply) return settle(cfg, reply);

      if (!isWrite(req.method) && PRACTICE_REFUSED_READS.some(pattern => pattern.test(req.path))) {
        engine.noteRefused(req);
        return settle(cfg, { status: 409, data: { success: false, error: PRACTICE_REFUSAL, code: 'PRACTICE_MODE' } });
      }

      if (isWrite(req.method)) {
        if (mustRefuse(req)) {
          engine.noteRefused(req);
          return settle(cfg, { status: 409, data: { success: false, error: PRACTICE_REFUSAL, code: 'PRACTICE_MODE' } });
        }
        return passThrough(cfg);
      }

      const res = await passThrough(cfg);
      if (engine.wantsOverlay(req)) {
        try {
          res.data = engine.overlay(req, parseResponseBody(res.data));
        } catch (err) {
          // An overlay bug costs the practice delta on one read, never the read.
          // eslint-disable-next-line no-console
          console.warn('[test meeting] overlay failed', req.path, err);
        }
      }
      return res;
    };
    return config;
  });
}
